import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

// Matches the shape returned by GET org/portfolios/
// Both sub-portfolios and programs use the same "children" nesting
export interface HierarchyNode {
  id: number;
  name: string;
  display_name?: string;
  owner?: { id: number; first_name: string; last_name: string };
  // subportfolio fields
  cost_center_name?: string;
  cost_center_code?: string;
  org_unit_type?: any;
  // program fields
  description?: string;
  children?: HierarchyNode[];
}

export interface UserOption {
  id: number;
  first_name: string;
  last_name: string;
  username?: string;
}

export interface OrgUnitType {
  id: number;
  name: string;
  display_name?: string;
}

@Injectable({ providedIn: 'root' })
export class HierarchyBrowserApiService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);

  private async headers(): Promise<HttpHeaders> {
    const { token } = await this.auth.ensureAuth();
    return new HttpHeaders({ Authorization: `JWT ${token}` });
  }

  async getHierarchy(): Promise<HierarchyNode[]> {
    const h = await this.headers();
    const raw = await firstValueFrom(
      this.http.get<any>(`${environment.keplerUrl}/org/portfolios/`, { headers: h })
    );
    return Array.isArray(raw) ? raw : (raw?.results ?? []);
  }

  async getUsers(): Promise<UserOption[]> {
    const h = await this.headers();
    const raw = await firstValueFrom(
      this.http.get<any>(`${environment.keplerUrl}/users/`, { headers: h })
    );
    return Array.isArray(raw) ? raw : (raw?.results ?? []);
  }

  async getOrgUnitTypes(): Promise<OrgUnitType[]> {
    const h = await this.headers();
    const raw = await firstValueFrom(
      this.http.get<any>(`${environment.keplerUrl}/base_value/?type=org_unit_type`, { headers: h })
    );
    return Array.isArray(raw) ? raw : (raw?.results ?? []);
  }

  async createPortfolio(payload: object): Promise<HierarchyNode> {
    const h = await this.headers();
    return firstValueFrom(
      this.http.post<HierarchyNode>(`${environment.keplerUrl}/org/portfolios/`, payload, { headers: h })
    );
  }

  async createSubPortfolio(portfolioId: number, payload: object): Promise<HierarchyNode> {
    const h = await this.headers();
    return firstValueFrom(
      this.http.post<HierarchyNode>(
        `${environment.keplerUrl}/org/portfolios/${portfolioId}/create-subportfolio/`,
        payload, { headers: h }
      )
    );
  }

  async createProgram(subPortfolioId: number, payload: object): Promise<HierarchyNode> {
    const h = await this.headers();
    return firstValueFrom(
      this.http.post<HierarchyNode>(
        `${environment.keplerUrl}/org/portfolios/${subPortfolioId}/create-program/`,
        payload, { headers: h }
      )
    );
  }

  async updatePortfolioOrSub(id: number, payload: object): Promise<HierarchyNode> {
    const h = await this.headers();
    return firstValueFrom(
      this.http.patch<HierarchyNode>(
        `${environment.keplerUrl}/org/portfolios/${id}/`,
        payload, { headers: h }
      )
    );
  }

  async updateProgram(id: number, payload: object): Promise<HierarchyNode> {
    const h = await this.headers();
    return firstValueFrom(
      this.http.put<HierarchyNode>(
        `${environment.keplerUrl}/org/programs/${id}/`,
        payload, { headers: h }
      )
    );
  }
}
